import PersonUi from './PersonUi'
import { PersonLiteDto } from '../api/person/models/PersonDto'
import Modal from './Modal'
import Cards from './Cards'
import Note from './Note'
import { NoteType } from '../enums/NoteType'
import { useState, useMemo, useEffect } from 'react'
import { normalizeText } from '../utils/stringUtils'
import LoadingSpinner, {
    LoadingCaption,
} from '../uiV2/common/loading/LoadingSpinner'

interface SelectPersonModalProps {
    persons: PersonLiteDto[]
    title: string
    onClose: () => void
    onSelectChanged: (person: PersonLiteDto, selected: boolean) => void
    isLoading?: boolean
    captions?: LoadingCaption[]
    errMsg?: string
    multiSelectButtons?: React.ReactNode
    searchTerm?: string
    onSearchTermChange?: (value: string) => void
    paginationControls?: React.ReactNode
}

function SelectPersonModal({
    persons,
    title,
    onClose,
    onSelectChanged,
    isLoading = false,
    captions,
    errMsg,
    multiSelectButtons,
    searchTerm: controlledSearchTerm,
    onSearchTermChange,
    paginationControls,
}: SelectPersonModalProps): React.JSX.Element {
    const [localSearchTerm, setLocalSearchTerm] = useState('')
    const isSearchControlled = controlledSearchTerm !== undefined
    const searchTerm = isSearchControlled
        ? controlledSearchTerm
        : localSearchTerm

    const handleSearchChange = (value: string) => {
        if (!isSearchControlled) {
            setLocalSearchTerm(value)
        }
        onSearchTermChange?.(value)
    }
    const filteredPersons = useMemo(() => {
        const term = normalizeText(searchTerm.trim())
        if (!term) return persons
        return persons.filter((person) => {
            const first = normalizeText(person.firstName || '')
            const last = normalizeText(person.lastName || '')
            return first.includes(term) || last.includes(term)
        })
    }, [searchTerm, persons])

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
    const [fadingOutIds, setFadingOutIds] = useState<Set<string>>(new Set())
    const [hasMounted, setHasMounted] = useState(false)
    useEffect(() => {
        setHasMounted(true)
    }, [])
    const fadeDurationMs = 300
    const showInitialMountLoading = !hasMounted && persons.length === 0
    const showDataLoading = isLoading && persons.length === 0
    const showLoadingState = showInitialMountLoading || showDataLoading

    const handleSelect = (person: PersonLiteDto) => {
        const id = person.id
        if (selectedIds.has(id)) return

        setSelectedIds((prev) => new Set(prev).add(id))
        setFadingOutIds((prev) => new Set(prev).add(id))

        onSelectChanged(person, true)

        setTimeout(() => {
            setFadingOutIds((prev) => {
                const updated = new Set(prev)
                updated.delete(id)
                return updated
            })
        }, fadeDurationMs)
    }

    const visiblePersons = filteredPersons.filter(
        (p) => !selectedIds.has(p.id) || fadingOutIds.has(p.id),
    )
    return (
        <Modal
            onClose={onClose}
            title={title}
            size="large"
            zIndexClassName="z-[60]"
            classes="h-[1000px] w-full"
        >
            <div className="flex flex-col items-start w-full">
                {multiSelectButtons}
                <div className="flex w-full mt-2">
                    <input
                        type="text"
                        className="form-input flex-grow"
                        placeholder="Search by name..."
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                    />
                </div>
                {paginationControls && (
                    <div className="mt-2 w-full">{paginationControls}</div>
                )}
                {showLoadingState ? (
                    <div className="flex w-full justify-center py-24">
                        <LoadingSpinner show captions={captions} />
                    </div>
                ) : (
                    <>
                        {errMsg && <Note type={NoteType.ERROR}>{errMsg}</Note>}
                        {filteredPersons.length === 0 ? (
                            <Note
                                type={NoteType.INFO}
                                containerClassNames="mt-2"
                            >
                                No people found matching your search
                            </Note>
                        ) : (
                            <Cards
                                className={
                                    multiSelectButtons
                                        ? 'mt-4 overflow-visible'
                                        : 'mt-2 overflow-visible'
                                }
                            >
                                {visiblePersons.map((person) => {
                                    const isSelected = selectedIds.has(
                                        person.id,
                                    )
                                    const isFading = fadingOutIds.has(person.id)

                                    return (
                                        <div
                                            key={person.id}
                                            className={`relative w-[300px] p-4 rounded-lg border transition-all duration-300 ease-out cursor-pointer ${
                                                isSelected
                                                    ? 'bg-gray-100 border-ctxt-blue/50'
                                                    : 'border-gray-300 hover:outline hover:outline-2 hover:outline-ctxt-blue'
                                            } ${
                                                isFading
                                                    ? 'opacity-0 scale-95 pointer-events-none'
                                                    : 'opacity-100 scale-100'
                                            }`}
                                            onClick={() => handleSelect(person)}
                                        >
                                            <PersonUi person={person} />
                                        </div>
                                    )
                                })}
                            </Cards>
                        )}
                    </>
                )}
            </div>
        </Modal>
    )
}

export default SelectPersonModal
